import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import Nav from './Nav';
import Footer from './Footer';

const FEEDBACK_EMAIL = 'hello@yourtop8.com';

function H2({ children }) {
  return <h2 style={{ fontSize: 17, fontWeight: 700, margin: '32px 0 8px', letterSpacing: '-.1px' }}>{children}</h2>;
}

function Feedback({ currentUser, currentUserProfile }) {
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState(currentUser?.email || '');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  // Sends straight to us via the submitFeedback Cloud Function (stored + emailed
  // server-side). No mailto, no mail app, no key in the client.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    try {
      const submit = httpsCallable(functions, 'submitFeedback');
      await submit({ message, contact, page: '/feedback' });
      setStatus('done');
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} />
      <div className="wrap" style={{ maxWidth: 680 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: '8px 0 4px' }}>Feedback</h1>
        <p style={{ color: 'var(--muted)', margin: '0 0 4px', fontSize: 15, lineHeight: 1.6 }}>
          Your Top 8 is in <strong style={{ color: 'var(--accent)' }}>Alpha</strong>. That means
          it's early, rough in places, and changing fast. You're one of a small group kicking the
          tires, and your honest reactions are the whole point.
        </p>

        <H2>Stuff we already know</H2>
        <p style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 12px' }}>
          The apps are coming. Yes, we need to build them out, and that's in the pipeline. The Android
          app is in closed testing right now, and an iPhone app is next. For the moment most people use
          the website, which works great in a phone browser. So no need to tell us "make an app."
        </p>

        <H2>Stuff that probably won't change</H2>
        <p style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.65, margin: '0 0 12px' }}>
          We're almost certainly not adding messaging or a Wall. If your big idea is DMs or a feed,
          it's a kind thought, but that's not what this is. There are already a million other sites
          you can use for that. This one? It's all about Your Top 8.
        </p>

        <H2>What actually helps us</H2>
        <ul style={{ color: 'var(--muted)', fontSize: 14.5, lineHeight: 1.7, margin: '0 0 8px', paddingLeft: 20 }}>
          <li>Anything that's broken or throws an error.</li>
          <li>Anything that looks bad, off, or out of place.</li>
          <li>Anything that didn't work right the first time you tried it.</li>
          <li>Genuine ideas that fit what Your Top 8 is about.</li>
        </ul>

        {status === 'done' ? (
          <div className="card" style={{ marginTop: 20, padding: 20 }}>
            <strong style={{ fontSize: 16 }}>Got it. Thank you, seriously.</strong>
            <p style={{ color: 'var(--muted)', fontSize: 14, margin: '8px 0 0', lineHeight: 1.6 }}>
              This kind of early feedback is exactly how Your Top 8 gets better.
            </p>
            <Link to="/" className="btn btn-sm" style={{ marginTop: 14, background: 'var(--card-2)', border: '1px solid var(--border)' }}>
              Back home
            </Link>
          </div>
        ) : !currentUser ? (
          <div className="card" style={{ marginTop: 20, padding: 20 }}>
            <strong style={{ fontSize: 16 }}>Sign in to send feedback</strong>
            <p style={{ color: 'var(--muted)', fontSize: 14, margin: '8px 0 0', lineHeight: 1.6 }}>
              We ask you to sign in so we can follow up and keep out spam. It only takes a second.
            </p>
            <Link to="/" className="btn btn-primary btn-sm" style={{ marginTop: 14 }}>
              Sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
            <textarea
              className="input"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="What's broken, what looks off, what didn't work, or what you'd love to see. The blunter the better."
              rows={6}
              maxLength={5000}
              style={{ width: '100%', resize: 'vertical', minHeight: 130, lineHeight: 1.5 }}
              required
            />
            <input
              className="input"
              type="text"
              value={contact}
              onChange={e => setContact(e.target.value)}
              placeholder="Email or handle (optional, so we can follow up)"
              style={{ width: '100%', marginTop: 10 }}
            />
            {status === 'error' && (
              <div className="form-error" style={{ marginTop: 8 }}>
                Something went wrong sending that. Email us at {FEEDBACK_EMAIL} instead.
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!message.trim() || status === 'sending'}
              style={{ marginTop: 14, fontSize: 15, padding: '12px 28px' }}
            >
              {status === 'sending' ? 'Sending...' : 'Send feedback'}
            </button>
          </form>
        )}
      </div>
      <Footer />
    </>
  );
}

export default Feedback;
